import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PaymentOptionsService } from 'src/app/services/payment-options/payment-options.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import Swal from 'sweetalert2';

// Interfaces para tipado
interface PaymentOption {
  id?: string;
  code: string;
  name: string;
  paymentTitle: string;
  paymentDescription: string;
  showClient: boolean;
  priority?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

interface PaymentOptionForm {
  code: string;
  name: string;
  paymentTitle: string;
  paymentDescription: string;
  showClient: boolean;
}

@Component({
  selector: 'app-payment-options',
  templateUrl: './payment-options.component.html',
  styleUrls: ['./payment-options.component.css']
})
export class PaymentOptionsComponent implements OnInit {
  paymentOptions: PaymentOption[] = [];
  loading = false;
  error: string | null = null;

  // Modal y formulario
  showModal = false;
  isEditing = false;
  currentPaymentOption: PaymentOption | null = null;
  
  // Formulario
  paymentOptionForm: PaymentOptionForm = {
    code: '',
    name: '',
    paymentTitle: '',
    paymentDescription: '',
    showClient: false
  };

  // Validaciones
  formErrors: { [key: string]: string } = {};
  formSubmitting = false;

  // Usuario y permisos
  sessionUser: any;
  isOwner = false;

  constructor(
    private paymentOptionsService: PaymentOptionsService,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Verificar permisos de usuario
    this.authService.auth$.subscribe((rep: any) => {
      if (rep) {
        this.sessionUser = this.authService.sessionUser;
        this.isOwner = this.authService.hasAccessOrProfile('finanzas', ['OWNER']);
      }
    });

    this.loadPaymentOptions();
  }

  /** Enlace a «Cuentas por contrato» (Perfil bancario): mismo permiso que la vista. */
  get canSeeAccountsByContract(): boolean {
    return this.authService.hasPermission('config.perfil_bancario.read');
  }

  /**
   * Verificar si el usuario actual es OWNER
   */
  canEdit(): boolean {
    return this.isOwner || this.authService.hasPermission('config.metodos_pago.update');
  }

  /**
   * Verificar si el usuario puede desactivar payment options
   */
  canToggle(): boolean {
    return this.isOwner || this.authService.hasPermission('config.metodos_pago.config');
  }

  /**
   * Cargar payment options desde el servidor
   */
  loadPaymentOptions = async () => {
    try {
      this.loading = true;
      this.error = null;
      
      const response = await this.paymentOptionsService.getPaymentOptions();
      
      if (response && response.data && response.data.paymentOptions) {
        // Ordenar por prioridad (menor número = mayor prioridad)
        this.paymentOptions = response.data.paymentOptions.sort((a: any, b: any) => {
          return (a.priority || 999) - (b.priority || 999);
        });
      } else {
        this.paymentOptions = [];
      }
      
    } catch (error) {
      console.error('Error loading payment options:', error);
      this.error = 'Error al cargar las opciones de pago';
      this.paymentOptions = [];
    } finally {
      this.loading = false;
    }
  };

  // ========== CRUD OPERATIONS (COMENTADAS TEMPORALMENTE) ==========

  /**
   * Abrir modal para crear nueva opción de pago
   * COMENTADO: No se usará por el momento
   */
  /*
  openCreateModal = () => {
    if (!this.canEdit()) {
      this.showErrorMessage('No tienes permisos para crear opciones de pago');
      return;
    }
    
    this.isEditing = false;
    this.currentPaymentOption = null;
    this.resetForm();
    this.showModal = true;
  };
  */

  /**
   * Abrir modal para editar opción de pago existente
   */
  openEditModal = (paymentOption: PaymentOption) => {
    if (!this.canEdit()) {
      this.showErrorMessage('No tienes permisos para editar opciones de pago');
      return;
    }

    this.isEditing = true;
    this.currentPaymentOption = paymentOption;
    this.populateForm(paymentOption);
    this.showModal = true;
  };

  /**
   * Cerrar modal
   */
  closeModal = () => {
    this.showModal = false;
    this.isEditing = false;
    this.currentPaymentOption = null;
    this.resetForm();
  };

  /**
   * Resetear formulario
   */
  resetForm = () => {
    this.paymentOptionForm = {
      code: '',
      name: '',
      paymentTitle: '',
      paymentDescription: '',
      showClient: false
    };
    this.formErrors = {};
  };

  /**
   * Poblar formulario con datos existentes
   */
  populateForm = (paymentOption: PaymentOption) => {
    this.paymentOptionForm = {
      code: paymentOption.code || '',
      name: paymentOption.name || '',
      paymentTitle: paymentOption.paymentTitle || '',
      paymentDescription: paymentOption.paymentDescription || '',
      showClient: paymentOption.showClient || false
    };
    this.formErrors = {};
  };

  /**
   * Validar formulario
   */
  validateForm = (): boolean => {
    this.formErrors = {};

    // Validar código
    if (!this.paymentOptionForm.code.trim()) {
      this.formErrors['code'] = 'El código es requerido';
    } else if (this.paymentOptionForm.code.length < 3) {
      this.formErrors['code'] = 'El código debe tener al menos 3 caracteres';
    } else if (!/^[A-Z0-9_]+$/.test(this.paymentOptionForm.code)) {
      this.formErrors['code'] = 'El código solo puede contener letras mayúsculas, números y guiones bajos';
    }

    // Validar nombre
    if (!this.paymentOptionForm.name.trim()) {
      this.formErrors['name'] = 'El nombre es requerido';
    } else if (this.paymentOptionForm.name.length < 3) {
      this.formErrors['name'] = 'El nombre debe tener al menos 3 caracteres';
    }

    // Validar título de pago
    if (!this.paymentOptionForm.paymentTitle.trim()) {
      this.formErrors['paymentTitle'] = 'El título de pago es requerido';
    } else if (this.paymentOptionForm.paymentTitle.length < 3) {
      this.formErrors['paymentTitle'] = 'El título debe tener al menos 3 caracteres';
    }

    // Validar descripción
    if (!this.paymentOptionForm.paymentDescription.trim()) {
      this.formErrors['paymentDescription'] = 'La descripción es requerida';
    } else if (this.paymentOptionForm.paymentDescription.length < 10) {
      this.formErrors['paymentDescription'] = 'La descripción debe tener al menos 10 caracteres';
    }

    // Validar código único (solo para crear)
    if (!this.isEditing) {
      const existingCode = this.paymentOptions.find(
        option => option.code.toLowerCase() === this.paymentOptionForm.code.toLowerCase()
      );
      if (existingCode) {
        this.formErrors['code'] = 'Ya existe una opción de pago con este código';
      }
    }

    return Object.keys(this.formErrors).length === 0;
  };

  /**
   * Guardar payment option (crear o actualizar)
   */
  savePaymentOption = async () => {
    if (!this.validateForm()) {
      return;
    }

    try {
      this.formSubmitting = true;

      if (this.isEditing && this.currentPaymentOption) {
        // Actualizar existente
        await this.paymentOptionsService.updatePaymentOption(
          this.currentPaymentOption.id!,
          this.paymentOptionForm
        );
        this.showSuccessMessage('Opción de pago actualizada exitosamente');
      } else {
        // Crear nueva
        await this.paymentOptionsService.createPaymentOption(this.paymentOptionForm);
        this.showSuccessMessage('Opción de pago creada exitosamente');
      }

      // Recargar datos y cerrar modal
      await this.loadPaymentOptions();
      this.closeModal();

    } catch (error: any) {
      console.error('Error saving payment option:', error);
      this.showErrorMessage(
        error?.error?.message || 'Error al guardar la opción de pago'
      );
    } finally {
      this.formSubmitting = false;
    }
  };

  /**
   * Eliminar opción de pago
   * COMENTADO: No se usará por el momento
   */
  /*
  deletePaymentOption = async (paymentOption: PaymentOption) => {
    if (!this.canEdit()) {
      this.showErrorMessage('No tienes permisos para eliminar opciones de pago');
      return;
    }

    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Quieres eliminar "${paymentOption.name}"? Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      this.loading = true;
      await this.paymentOptionsService.deletePaymentOption(paymentOption.id!);
      this.showSuccessMessage('Opción de pago eliminada exitosamente');
      await this.loadPaymentOptions();
    } catch (error: any) {
      console.error('Error deleting payment option:', error);
      this.showErrorMessage(
        error?.error?.message || 'Error al eliminar la opción de pago'
      );
    } finally {
      this.loading = false;
    }
  };
  */

  // ========== EXISTING METHODS ==========

  /**
   * Verificar si una payment option está activa
   */
  isPaymentOptionActive = (paymentOption: PaymentOption): boolean => {
    return paymentOption.showClient === true;
  };

  /**
   * Activar/Desactivar una opción de pago
   */
  togglePaymentOption = async (paymentOption: PaymentOption) => {
    if (!this.canToggle()) {
      this.showErrorMessage('No tienes permisos para cambiar el estado de opciones de pago');
      return;
    }

    try {
      this.loading = true;
      
      // Calcular el nuevo estado (invertir el actual)
      const newStatus = !this.isPaymentOptionActive(paymentOption);
      
      // Llamar al servicio para hacer toggle
      await this.paymentOptionsService.togglePaymentOption(paymentOption.id!, newStatus);
      
      // Recargar los datos desde el servidor para obtener el estado actualizado
      await this.loadPaymentOptions();
      
    } catch (error) {
      console.error('Error toggling payment option:', error);
      this.showErrorMessage('Error al cambiar el estado de la opción de pago');
    } finally {
      this.loading = false;
    }
  };

  /**
   * Cambiar prioridad de un payment option
   */
  changePriority = async (paymentOption: PaymentOption, newPriority: number) => {
    try {
      this.loading = true;
      
      // Validar que la prioridad esté en el rango válido
      const maxPriority = this.getActivePaymentOptionsCount();
      if (newPriority < 1 || newPriority > maxPriority) {
        this.showErrorMessage(`La prioridad debe estar entre 1 y ${maxPriority}`);
        return;
      }
      
      // Llamar al servicio para cambiar la prioridad
      const response = await this.paymentOptionsService.updatePaymentOptionPriority(
        paymentOption.id!, 
        newPriority
      );
      
      if (response && response.ok) {
        // Recargar los datos desde el servidor para obtener el estado actualizado
        await this.loadPaymentOptions();
        console.log('✅ Prioridad actualizada exitosamente');
      } else {
        this.showErrorMessage(response?.message || 'Error al cambiar la prioridad');
      }
      
    } catch (error: any) {
      console.error('Error changing priority:', error);
      this.showErrorMessage(error?.error?.message || 'Error al cambiar la prioridad');
    } finally {
      this.loading = false;
    }
  };

  /**
   * Subir prioridad (decrementar número)
   */
  moveUp = async (paymentOption: PaymentOption) => {
    const currentPriority = paymentOption.priority || 999;
    const newPriority = Math.max(1, currentPriority - 1);
    
    if (newPriority !== currentPriority) {
      await this.changePriority(paymentOption, newPriority);
    }
  };

  /**
   * Bajar prioridad (incrementar número)
   */
  moveDown = async (paymentOption: PaymentOption) => {
    const currentPriority = paymentOption.priority || 999;
    const maxPriority = this.getActivePaymentOptionsCount();
    const newPriority = Math.min(maxPriority, currentPriority + 1);
    
    if (newPriority !== currentPriority) {
      await this.changePriority(paymentOption, newPriority);
    }
  };

  /**
   * Verificar si se puede subir la prioridad
   */
  canMoveUp = (paymentOption: PaymentOption): boolean => {
    const currentPriority = paymentOption.priority || 999;
    return currentPriority > 1 && this.isPaymentOptionActive(paymentOption);
  };

  /**
   * Verificar si se puede bajar la prioridad
   */
  canMoveDown = (paymentOption: PaymentOption): boolean => {
    const currentPriority = paymentOption.priority || 999;
    const maxPriority = this.getActivePaymentOptionsCount();
    return currentPriority < maxPriority && this.isPaymentOptionActive(paymentOption);
  };

  /**
   * Obtener el número de payment options activas
   */
  getActivePaymentOptionsCount = (): number => {
    return this.paymentOptions.filter(option => this.isPaymentOptionActive(option)).length;
  };

  /**
   * Obtener la posición actual de una payment option
   */
  getCurrentPosition = (paymentOption: PaymentOption): number => {
    return paymentOption.priority || 999;
  };

  /**
   * Navegar a la gestión de cuentas de una payment option
   */
  manageAccounts = (paymentOption: PaymentOption) => {
    this.router.navigate(['/console/payment-options', paymentOption.id, 'accounts']);
  };

  manageAsyncBDV = (paymentOption: PaymentOption) => {
    this.router.navigate(['/console/bdv-async-management']);
  };


  /**
   * Obtener el ícono según el tipo de payment option
   */
  getPaymentOptionIcon = (code: string): string => {
    switch (code) {
      case 'BOTON_PAGOS_VENEZUELA':
        return '🏦';
      case 'BOTON_PAGOS_MERCANTIL':
        return '💳';
      case 'ALL_PAYMENT_METHODS':
        return '💰';
      default:
        return '💳';
    }
  };

  /**
   * Obtener el color del badge según el estado
   */
  getStatusBadgeClass = (paymentOption: PaymentOption): string => {
    return this.isPaymentOptionActive(paymentOption) 
      ? 'badge-success' 
      : 'badge-secondary';
  };

  /**
   * Obtener el texto del estado
   */
  getStatusText = (paymentOption: PaymentOption): string => {
    return this.isPaymentOptionActive(paymentOption) ? 'Activo' : 'Inactivo';
  };

  /**
   * Mostrar mensaje de éxito
   */
  private showSuccessMessage = (message: string) => {
    console.log('✅ Success:', message);
    Swal.fire({
      title: '¡Éxito!',
      text: message,
      icon: 'success',
      confirmButtonColor: '#01abce',
      confirmButtonText: 'OK',
      timer: 3000,
      timerProgressBar: true
    });
  };

  /**
   * Mostrar mensaje de error
   */
  private showErrorMessage = (message: string) => {
    console.error('❌ Error:', message);
    Swal.fire({
      title: 'Error',
      text: message,
      icon: 'error',
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'OK'
    });
  };
} 